import React, { useEffect, useRef, useState } from "react";
import Lenis from "@studio-freight/lenis";
import "./turfParkTheme.css";
import OpenMapsLink from "./OpenMapsLink";
import OngroundStoreIcon from "../OngroundStoreIcon";

// Import Assets
import heroImg from "./turfpark-assets/turf-park.jpg";
import futsalImg from "./turfpark-assets/turf-park-g1.jpg";
import basketballImg from "./turfpark-assets/turf-park-g2.jpg";
import pickleballImg from "./turfpark-assets/turf-park-g3.jpg";
import appMockup from "./turfpark-assets/app-mockup.png";
import ongroundLogo from "../assets/branding/onground-powered-by-dark-bg.png";
import {
  resolveOngroundAppLink,
  resolveVenueSocialLink,
  resolveThemeArray,
  resolveThemeCopy,
  resolveThemeLogo,
  resolveThemeMedia,
  resolveThemeObject,
} from "../publicWebsiteContent";
import { resolveThemePaletteStyle } from "../themePalette";
import { isWebsiteSectionVisible } from "../themeDefinitions";
import usePublicContactEnquiry from "../usePublicContactEnquiry";
import PublicContactEnquiryStatus, { PublicContactHoneypot, PublicContactPhoneField } from "../PublicContactEnquiryStatus";

// Exact original fallbacks.
const STATIC_GROUNDS = [
  {
    id: "futsal",
    img: futsalImg,
    sport: "Football",
    name: "Futsal / Cricksal",
    price: "NPR 1,400 / hr",
  },
  {
    id: "basketball",
    img: basketballImg,
    sport: "Basketball",
    name: "Basketball Court",
    price: "NPR 1,000 / hr",
  },
  {
    id: "pickleball",
    img: pickleballImg,
    sport: "Pickleball",
    name: "Pickleball Court",
    price: "NPR 1,000 / hr",
  },
];

const STATIC_AMENITIES = [
  "Free Parking",
  "Washroom",
  "Changing Room",
  "Floodlights",
  "League Ready",
  "Instant Booking",
  "Open Daily",
  "5.0 Rated",
  "3 Sports",
  "Indoor Courts",
];

const STATIC_STATS = [
  { n: 3, suffix: "", label: "Sports Courts" },
  { n: 5, suffix: ".0", label: "Star Rating" },
  { n: 1000, suffix: "+", label: "Starting (NPR)" },
  { n: 15, suffix: "h", label: "Open Per Day" },
];

const STATIC_TESTIMONIALS = [
  {
    name: "Sanjay Maharjan",
    sport: "Futsal",
    text: "Best futsal courts in Lalitpur. The turf quality is excellent and the floodlights make evening games absolutely perfect.",
  },
  {
    name: "Prashant Shrestha",
    sport: "Basketball",
    text: "Great facility! The basketball court is well-marked and maintained. Staff is very friendly. Highly recommend for group play.",
  },
  {
    name: "Nisha Thapa",
    sport: "Pickleball",
    text: "Love the pickleball courts here! Perfect surface and great atmosphere. We come every weekend as a group and are never disappointed.",
  },
];

const STATIC_FAQS = [
  {
    q: "What sports are available at Turf Park?",
    a: "We have three premium courts: Futsal/Cricksal, Basketball, and Pickleball. All courts are indoor and well-maintained.",
  },
  {
    q: "What are the opening hours?",
    a: "Turf Park is open every day from 6:00 AM to 9:00 PM, including public holidays.",
  },
  {
    q: "How do I book a court?",
    a: "Book easily through the ONGROUND app or call us at +977 9823537944. Slots can be reserved 24/7 online.",
  },
  {
    q: "Is parking available?",
    a: "Yes, free parking is available on-site for all visitors.",
  },
  {
    q: "What is the cancellation policy?",
    a: "Free cancellation up to 4 hours before your slot. Cancellations within 4 hours incur a 50% charge.",
  },
];

const TURF_PARK_THEME_ID = "turf-park";
const STATIC_MAP_EMBED_URL =
  "https://maps.google.com/maps?q=Dholahiti,Lalitpur,Nepal&z=15&output=embed";

const FACILITY_LABELS = Object.freeze({
  parking: "Free Parking",
  washroom: "Washroom",
  changing_room: "Changing Room",
  lights: "Floodlights",
  cafeteria: "Cafeteria",
  equipment_rental: "Equipment Rental",
});

const cleanText = (value) =>
  String(value == null ? "" : value).trim();

const formatLabel = (value) =>
  cleanText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeKey = (value) =>
  cleanText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const uniqueValues = (values) =>
  Array.from(
    new Set(
      values
        .map((value) => cleanText(value))
        .filter(Boolean),
    ),
  );

const formatTime = (value, fallback) => {
  const input = cleanText(value);

  if (!/^\d{1,2}:\d{2}$/.test(input)) {
    return input || fallback;
  }

  const [hourText, minute] = input.split(":");
  const hour = Number(hourText);

  if (!Number.isFinite(hour)) {
    return fallback;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const getOpenHoursDuration = (openTime, closeTime) => {
  const parseMinutes = (value) => {
    const match = cleanText(value).match(/^(\d{1,2}):(\d{2})$/);

    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return hour * 60 + minute;
  };

  const start = parseMinutes(openTime);
  const end = parseMinutes(closeTime);

  if (start == null || end == null) {
    return null;
  }

  const durationMinutes =
    end > start
      ? end - start
      : end === start
        ? 24 * 60
        : end + 24 * 60 - start;

  return Math.round(durationMinutes / 60);
};

const getVenuePhone = (venue) =>
  cleanText(venue?.contact?.e164) ||
  cleanText(venue?.contact?.nationalNumber) ||
  "";

const getVenueAddress = (venue) => {
  const address = cleanText(venue?.location?.address);

  if (address) return address;

  return [
    cleanText(venue?.location?.city),
    cleanText(venue?.location?.country || venue?.country),
  ]
    .filter(Boolean)
    .join(", ");
};

const getVenueLocality = (venue) =>
  cleanText(venue?.location?.address).split(",")[0] ||
  cleanText(venue?.location?.city) ||
  "";

const getVenueRegion = (venue) =>
  [
    cleanText(venue?.location?.city),
    cleanText(venue?.location?.country || venue?.country),
  ]
    .filter(Boolean)
    .join(", ");

const getSafeExternalUrl = (value, fallback = "#") => {
  const input = cleanText(value);

  if (!input || input === "#") {
    return fallback;
  }

  try {
    const parsed = new URL(input);

    if (!["http:", "https:"].includes(parsed.protocol)) {
      return fallback;
    }

    return parsed.toString();
  } catch {
    return fallback;
  }
};

const buildMapEmbedUrl = (venue, configuredUrl) => {
  const rawLatitude = venue?.location?.latitude;
  const rawLongitude = venue?.location?.longitude;
  const latitude = rawLatitude === null || rawLatitude === undefined || rawLatitude === ""
    ? Number.NaN
    : Number(rawLatitude);
  const longitude = rawLongitude === null || rawLongitude === undefined || rawLongitude === ""
    ? Number.NaN
    : Number(rawLongitude);
  const hasValidCoordinates =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  if (hasValidCoordinates) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}&z=15&output=embed`;
  }

  const safeConfiguredUrl = getSafeExternalUrl(configuredUrl, "");
  if (safeConfiguredUrl) {
    return safeConfiguredUrl;
  }

  const address = getVenueAddress(venue);
  return address
    ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`
    : STATIC_MAP_EMBED_URL;
};

const getGroundImage = (ground) =>
  cleanText(ground?.mainImageUrl || ground?.imageUrl);

const getGroundSportLabel = (ground) =>
  formatLabel(
    ground?.sportName ||
      ground?.sport?.name ||
      ground?.type,
  );

const formatGroundPrice = (ground, venue) => {
  const rawAmount =
    ground?.basePrice ??
    ground?.price ??
    ground?.pricing?.basePrice ??
    venue?.pricing?.basePrice;

  if (
    rawAmount === undefined ||
    rawAmount === null ||
    rawAmount === ""
  ) {
    return "";
  }

  const amount = Number(rawAmount);

  if (!Number.isFinite(amount) || amount < 0) {
    return "";
  }

  const currency = cleanText(
    ground?.currency ||
      ground?.pricing?.currency ||
      venue?.pricing?.currency,
  ).toUpperCase();

  const unit = cleanText(ground?.unit).toLowerCase();
  const unitLabel = unit === "slot" ? "slot" : "hr";

  return `${currency || "NPR"} ${amount.toLocaleString("en-US")} / ${unitLabel}`;
};

const splitVenueBrand = (venueName) => {
  const parts = cleanText(venueName).split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return {
      primary: "Turf",
      highlighted: "Park",
    };
  }

  return {
    primary: parts[0],
    highlighted: parts.slice(1).join(" "),
  };
};

const getRatingParts = (rating) => {
  const fixed = rating.toFixed(1);
  const [whole, decimal] = fixed.split(".");

  return {
    whole: Number(whole),
    suffix: `.${decimal}`,
    text: fixed,
  };
};

function useScrollAnimation(disabled = false) {
  useEffect(() => {
    if (disabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("tp-in-view");
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -40px 0px",
      },
    );

    document
      .querySelectorAll("[data-animate]")
      .forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [disabled]);
}

function useCountUp(target, isActive) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isActive) return undefined;

    let start = 0;
    const increment = target / (2000 / 16);

    const timer = window.setInterval(() => {
      start += increment;

      if (start >= target) {
        setCount(target);
        window.clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => window.clearInterval(timer);
  }, [isActive, target]);

  return count;
}

function StatItem({ n, suffix, label, isActive }) {
  const count = useCountUp(n, isActive);

  return (
    <div className="tp-stat-item" data-animate="fade-up">
      <p className="tp-stat-num">{count}{suffix}</p>
      <p className="tp-stat-label">{label}</p>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`tp-faq-item ${open ? "open" : ""}`}>
      <button
        className="tp-faq-trigger"
        onClick={() => setOpen(!open)}
      >
        <span>{q}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className="tp-faq-icon"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div className="tp-faq-body"><p>{a}</p></div>
    </div>
  );
}

const TurfParkTheme = ({
  venue = {},
  grounds = [],
  website = {},
  primaryColor = "#2FE5A7",
  accentColor = "#06090F",
  heroImage: commonHeroImage = "",
  openBooking = () => {},
  previewMode = false,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [statsActive, setStatsActive] = useState(false);
  const statsRef = useRef(null);
  const heroRef = useRef(null);

  const themePaletteStyle = resolveThemePaletteStyle(
    TURF_PARK_THEME_ID,
    primaryColor,
    accentColor,
  );

  const t = (key, fallback, apiValue = "") =>
    resolveThemeCopy(
      website,
      TURF_PARK_THEME_ID,
      key,
      fallback,
      apiValue,
    );

  const m = (key, fallback) =>
    resolveThemeMedia(
      website,
      TURF_PARK_THEME_ID,
      key,
      fallback,
    );

  const {
    values: formData,
    handleChange: handleFormChange,
    submit: handleFormSubmit,
    submitting: contactSubmitting,
    feedback: contactFeedback,
  } = usePublicContactEnquiry({
    venue,
    previewMode,
    successMessage: t(
      "formSuccessMessage",
      "Message sent! We'll get back to you shortly.",
    ),
  });

  const social = resolveThemeObject(
    website,
    TURF_PARK_THEME_ID,
    "social",
    {},
  );

  const venueName = cleanText(venue?.name) || "Turf Park";
  const brand = splitVenueBrand(venueName);
  const venueAddress =
    getVenueAddress(venue) || "Dholahiti, Lalitpur";
  const venueLogoSub =
    getVenueRegion(venue) || "Lalitpur, Nepal";
  const venueLocality =
    getVenueLocality(venue) || "Dholahiti, Lalitpur";
  const venuePhone =
    getVenuePhone(venue) || "+977 9823537944";

  const rawOpenTime =
    cleanText(venue?.policies?.openTime) || "06:00";
  const rawCloseTime =
    cleanText(venue?.policies?.closeTime) || "21:00";

  const openingTime = formatTime(rawOpenTime, "6:00 AM");
  const closingTime = formatTime(rawCloseTime, "9:00 PM");
  const openHoursDuration =
    getOpenHoursDuration(rawOpenTime, rawCloseTime) ||
    STATIC_STATS[3].n;

  const venueGrounds = Array.isArray(grounds) ? grounds : [];
  const gallery = Array.isArray(venue?.media?.gallery)
    ? venue.media.gallery.filter(Boolean)
    : [];

  const groundCards = (
    venueGrounds.length > 0
      ? venueGrounds.map((ground, index) => {
          const fallback =
            STATIC_GROUNDS[index % STATIC_GROUNDS.length];

          return {
            id:
              cleanText(ground?._id || ground?.id) ||
              `ground-${index + 1}`,
            ground,
            img:
              getGroundImage(ground) ||
              gallery[index] ||
              fallback.img,
            sport:
              getGroundSportLabel(ground) ||
              fallback.sport,
            name:
              cleanText(ground?.name) ||
              fallback.name,
            price:
              formatGroundPrice(ground, venue) ||
              fallback.price,
          };
        })
      : STATIC_GROUNDS.map((ground) => ({
          ...ground,
          ground: null,
        }))
  ).filter((ground) => ground.name && ground.img);

  const uniqueSports = uniqueValues(
    groundCards.map((ground) => ground.sport),
  );

  const groundCount =
    venueGrounds.length || STATIC_STATS[0].n;
  const sportCount =
    uniqueSports.length || STATIC_GROUNDS.length;

  const minimumGroundPrice = venueGrounds
    .map((ground) =>
      Number(
        ground?.basePrice ??
          ground?.price ??
          ground?.pricing?.basePrice,
      ),
    )
    .filter(
      (amount) =>
        Number.isFinite(amount) && amount >= 0,
    )
    .sort((left, right) => left - right)[0];

  const resolvedStartingPrice = Number(
    minimumGroundPrice ?? venue?.pricing?.basePrice,
  );

  const startingPrice =
    Number.isFinite(resolvedStartingPrice) &&
    resolvedStartingPrice >= 0
      ? resolvedStartingPrice
      : STATIC_STATS[2].n;

  const startingCurrency =
    cleanText(
      venueGrounds.find((ground) => {
        const amount = Number(
          ground?.basePrice ??
            ground?.price ??
            ground?.pricing?.basePrice,
        );

        return amount === minimumGroundPrice;
      })?.currency ||
        venue?.pricing?.currency,
    ).toUpperCase() || "NPR";

  const configuredRating = Number(website?.arena?.rating);
  const configuredReviewCount = Number(
    website?.arena?.reviewCount,
  );

  const rating =
    Number.isFinite(configuredRating) &&
    configuredRating > 0
      ? Math.min(5, configuredRating)
      : STATIC_STATS[1].n;

  const reviewCount =
    Number.isFinite(configuredReviewCount) &&
    configuredReviewCount > 0
      ? Math.floor(configuredReviewCount)
      : 26;

  const ratingParts = getRatingParts(rating);

  const stats = [
    {
      n: groundCount,
      suffix: "",
      label: t(
        "statsCourtsLabel",
        "Sports Courts",
      ),
    },
    {
      n: ratingParts.whole,
      suffix: ratingParts.suffix,
      label: t(
        "statsRatingLabel",
        "Star Rating",
      ),
    },
    {
      n: startingPrice,
      suffix: "+",
      label: t(
        "statsStartingLabel",
        "Starting (NPR)",
        `Starting (${startingCurrency})`,
      ),
    },
    {
      n: openHoursDuration,
      suffix: "h",
      label: t(
        "statsHoursLabel",
        "Open Per Day",
      ),
    },
  ];

  const apiFacilities = Array.isArray(venue?.facilities)
    ? venue.facilities
        .map((facility) => {
          const key = normalizeKey(facility);

          return (
            FACILITY_LABELS[key] ||
            formatLabel(facility)
          );
        })
        .filter(Boolean)
    : [];

  const apiAmenities =
    venueGrounds.length > 0 || apiFacilities.length > 0
      ? uniqueValues([
          ...apiFacilities,
          t("leagueReadyLabel", "League Ready"),
          website?.booking?.enabled !== false
            ? t("instantBookingLabel", "Instant Booking")
            : "",
          t("openDailyLabel", "Open Daily"),
          `${ratingParts.text} ${t("ratedLabel", "Rated")}`,
          `${sportCount} ${t("sportsLabel", "Sports")}`,
          t("indoorCourtsLabel", "Indoor Courts"),
        ])
      : [];

  const amenities =
    apiAmenities.length > 0
      ? apiAmenities
      : STATIC_AMENITIES;

  const testimonials = resolveThemeArray(
    website,
    TURF_PARK_THEME_ID,
    "testimonials",
    STATIC_TESTIMONIALS,
  ).map((testimonial, index) => {
    const fallback =
      STATIC_TESTIMONIALS[
        index % STATIC_TESTIMONIALS.length
      ];

    return {
      name:
        cleanText(testimonial?.name) ||
        fallback.name,
      sport:
        cleanText(
          testimonial?.sport || testimonial?.role,
        ) || fallback.sport,
      text:
        cleanText(testimonial?.text) ||
        fallback.text,
    };
  });

  const sportsSummary =
    uniqueSports.length > 0
      ? uniqueSports.join(", ")
      : "Futsal/Cricksal, Basketball, and Pickleball";

  const apiFaqs = STATIC_FAQS.map((faq, index) => {
    if (index === 0 && venueGrounds.length > 0) {
      return {
        ...faq,
        q: `What sports are available at ${venueName}?`,
        a: `${venueName} offers ${groundCount} ${groundCount === 1 ? "court" : "courts"} for ${sportsSummary}.`,
      };
    }

    if (index === 1) {
      return {
        ...faq,
        a: `${venueName} is open every day from ${openingTime} to ${closingTime}, including public holidays.`,
      };
    }

    if (index === 2 && getVenuePhone(venue)) {
      return {
        ...faq,
        a: `Book easily through the ONGROUND app or call us at ${venuePhone}. Slots can be reserved 24/7 online.`,
      };
    }

    if (index === 3 && apiFacilities.length > 0) {
      const parkingAvailable = venue.facilities.some(
        (facility) =>
          normalizeKey(facility) === "parking",
      );

      return {
        ...faq,
        a: parkingAvailable
          ? "Yes, free parking is available on-site for all visitors."
          : "Parking is not currently listed among the venue facilities. Please contact the venue to confirm availability.",
      };
    }

    if (
      index === 4 &&
      cleanText(venue?.policies?.cancellationPolicy)
    ) {
      return {
        ...faq,
        a: cleanText(
          venue.policies.cancellationPolicy,
        ),
      };
    }

    return faq;
  });

  const faqs = resolveThemeArray(
    website,
    TURF_PARK_THEME_ID,
    "faqs",
    STATIC_FAQS,
    apiFaqs,
  ).map((faq, index) => {
    const fallback =
      STATIC_FAQS[index % STATIC_FAQS.length];

    return {
      q:
        cleanText(faq?.q || faq?.question) ||
        fallback.q,
      a:
        cleanText(faq?.a || faq?.answer) ||
        fallback.a,
    };
  });

  const navigation = [
    {
      id: "grounds",
      sectionKey: "grounds",
      label: t("navGrounds", "Grounds"),
    },
    {
      id: "about",
      sectionKey: "about",
      label: t("navAbout", "About"),
    },
    {
      id: "contact",
      sectionKey: "contact",
      label: t("navContact", "Contact"),
    },
  ].filter(({ sectionKey }) =>
    isWebsiteSectionVisible(website, sectionKey),
  );

  const apiGroundsDescription =
    venueGrounds.length > 0
      ? `${groundCount} premium ${groundCount === 1 ? "court" : "courts"} across ${sportCount} ${sportCount === 1 ? "sport" : "sports"}. One incredible venue.`
      : "";

  const apiHeroDescription =
    uniqueSports.length > 0
      ? `${sportsSummary} — all under one roof.\n${cleanText(venue?.shortDescription || venue?.fullDescription) || "Kathmandu Valley's premier indoor sports arena."}`
      : cleanText(
          venue?.shortDescription ||
            venue?.fullDescription,
        );

  const heroBadgeApi =
    `${ratingParts.text} Rating - ${reviewCount} Reviews - ${venueLocality}`;

  const resolvedHeroImage = m(
    "heroImageUrl",
    cleanText(commonHeroImage) ||
      cleanText(venue?.media?.bannerImage) ||
      gallery[0] ||
      heroImg,
  );

  const resolvedAboutDescription = t(
    "aboutDescription",
    "A popular multi-sport indoor arena in Dholahiti, Lalitpur. Turf Park offers quality futsal, basketball, and pickleball courts on well-maintained surfaces. It is a vibrant, energetic venue perfect for casual play, league matches, and team practices across the Kathmandu Valley.",
    cleanText(
      venue?.fullDescription ||
        venue?.shortDescription,
    ),
  );

  const resolvedAppDescription = t(
    "appDescription",
    "Download the ONGROUND app and reserve Turf Park courts in seconds. Explore hundreds of sports venues across the Kathmandu Valley right from your pocket.",
    `Download the ONGROUND app and reserve ${venueName} courts in seconds. Explore sports venues and manage your bookings right from your pocket.`,
  );

  const resolvedFooterTagline = t(
    "footerTagline",
    "Play. Compete. Repeat.",
    cleanText(venue?.shortDescription),
  );

  const resolvedVenueLogo = resolveThemeLogo(
    website,
    TURF_PARK_THEME_ID,
    "",
  );

  const resolvedAppImage = m(
    "appImageUrl",
    appMockup,
  );

  const resolvedPoweredLogo = m(
    "poweredLogoUrl",
    ongroundLogo,
  );

  const mapEmbedUrl = buildMapEmbedUrl(
    venue,
    social?.mapEmbedUrl,
  );

  const appStoreUrl = resolveOngroundAppLink(
    social?.appStoreUrl,
    "appStore",
  );

  const googlePlayUrl = resolveOngroundAppLink(
    social?.googlePlayUrl,
    "googlePlay",
  );

  const footerSocialItems = [
    {
      key: "instagram",
      label: t("instagramLabel", "Instagram"),
      href: resolveVenueSocialLink(
        social?.instagramUrl,
        "instagram",
      ),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
      ),
    },
    {
      key: "facebook",
      label: t("facebookLabel", "Facebook"),
      href: resolveVenueSocialLink(
        social?.facebookUrl,
        "facebook",
      ),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
      ),
    },
    {
      key: "tiktok",
      label: t("tiktokLabel", "TikTok"),
      href: resolveVenueSocialLink(
        social?.tiktokUrl,
        "tiktok",
      ),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
      ),
    },
    {
      key: "whatsapp",
      label: t("whatsappLabel", "WhatsApp"),
      href: resolveVenueSocialLink(
        social?.whatsappUrl,
        "whatsapp",
      ),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
      ),
    },
  ].filter((item) => item.href);

  const canBook =
    website?.booking?.enabled !== false &&
    typeof openBooking === "function";

  const scrollTo = (id) => {
    if (previewMode) return;

    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => {
    if (previewMode) return;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const openGroundBooking = (ground = null) => {
    if (previewMode) return;

    if (canBook) {
      openBooking(ground);
      return;
    }

    scrollTo("contact");
  };

  useScrollAnimation(previewMode);

  useEffect(() => {
    if (previewMode) return undefined;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (time) =>
        Math.min(1, 1.001 - Math.pow(2, -10 * time)),
      smooth: true,
    });

    let animationFrameId = 0;

    const raf = (time) => {
      lenis.raf(time);
      animationFrameId =
        window.requestAnimationFrame(raf);
    };

    animationFrameId =
      window.requestAnimationFrame(raf);

    return () => {
      window.cancelAnimationFrame(
        animationFrameId,
      );
      lenis.destroy();
    };
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) return undefined;

    const onScroll = () => {
      setScrolled(window.scrollY > 60);

      if (heroRef.current) {
        const yPos = window.scrollY * 0.4;
        heroRef.current.style.transform =
          `translateY(${yPos}px)`;
      }
    };

    onScroll();
    window.addEventListener(
      "scroll",
      onScroll,
      { passive: true },
    );

    return () =>
      window.removeEventListener(
        "scroll",
        onScroll,
      );
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) {
      setStatsActive(true);
      return undefined;
    }

    if (!statsRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsActive(true);
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, [previewMode]);

  const phonePlaceholder =
    cleanText(venue?.contact?.dialCode)
      ? `${cleanText(venue.contact.dialCode).startsWith("+") ? "" : "+"}${cleanText(venue.contact.dialCode)} XXXXX XXXXX`
      : "+977 XXXXX XXXXX";

  const heroTitleLineOne = t("heroTitleLineOne", "YOUR COURT");
  const keepHeroTitleLineOneTogether =
    cleanText(heroTitleLineOne).toUpperCase() === "YOUR COURT";
  const groundsTitle = t("groundsTitle", "CHOOSE YOUR SPORT.");
  const keepGroundsTitleTogether =
    cleanText(groundsTitle).toUpperCase() === "CHOOSE YOUR SPORT.";

  return (
    <div
      className={`tp-theme tp-bg-dark ${previewMode ? "tp-theme-preview" : ""}`}
      style={themePaletteStyle}
    >
      {/* NAVBAR */}
      <header className={`tp-nav ${scrolled ? "scrolled" : ""}`}>
        <div className="tp-nav-inner">
          <button className="tp-logo" onClick={scrollToTop}>
            {resolvedVenueLogo ? (
              <img
                className="tp-logo-image"
                src={resolvedVenueLogo}
                alt={`${venueName} logo`}
              />
            ) : (
              <span className="tp-logo-dot"></span>
            )}
            <div>
              <span className="tp-logo-name">
                {brand.primary.toUpperCase()}
                <span className="tp-logo-accent">
                  {brand.highlighted.toUpperCase()}
                </span>
              </span>
              <span className="tp-logo-sub">{venueLogoSub}</span>
            </div>
          </button>

          <nav className="tp-nav-links">
            {navigation.map((link) => (
              <button
                key={link.id}
                className="tp-nav-link"
                onClick={() => scrollTo(link.id)}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <button
            className="tp-btn-primary tp-btn-sm"
            onClick={() => openGroundBooking()}
          >
            {t("navReserveLabel", "Reserve Court")}
          </button>
        </div>
      </header>

      {/* HERO */}
      {isWebsiteSectionVisible(website, "hero") && (
      <section className="tp-hero">
        <div className="tp-hero-bg-wrap">
          <div
            className="tp-hero-bg"
            ref={heroRef}
            style={{
              backgroundImage:
                `url(${resolvedHeroImage})`,
            }}
          ></div>
          <div className="tp-hero-overlay"></div>
        </div>

        <div className="tp-hero-content">
          <div
            className="tp-hero-badge"
            data-animate="fade-down"
          >
            {t(
              "heroBadge",
              "5.0 Rating - 26 Reviews - Dholahiti, Lalitpur",
              heroBadgeApi,
            )}
          </div>

          <h1
            className="tp-hero-title"
            data-animate="fade-up"
          >
            <span className={keepHeroTitleLineOneTogether ? "tp-hero-title-line-one" : undefined}>{heroTitleLineOne}</span><br />
            <span className="tp-text-green">
              {t("heroTitleHighlight", "AWAITS.")}
            </span>
          </h1>

          <p
            className="tp-hero-sub"
            data-animate="fade-up"
            style={{ "--delay": "0.15s" }}
          >
            {t(
              "heroDescription",
              "Futsal, Basketball, and Pickleball — all under one roof.\nKathmandu Valley's premier indoor sports arena.",
              apiHeroDescription,
            )
              .split("\n")
              .map((line, index, lines) => (
                <React.Fragment key={`${line}-${index}`}>
                  {line}
                  {index < lines.length - 1 && <br />}
                </React.Fragment>
              ))}
          </p>

          <div
            className="tp-hero-actions"
            data-animate="fade-up"
            style={{ "--delay": "0.3s" }}
          >
            <button
              className="tp-btn-primary tp-btn-lg"
              onClick={() => openGroundBooking()}
            >
              {t("heroReserveLabel", "Reserve Now")}
            </button>
            {isWebsiteSectionVisible(website, "grounds") && (
              <button
                className="tp-btn-outline tp-btn-lg"
                onClick={() => scrollTo("grounds")}
              >
                {t("heroViewGroundsLabel", "View Grounds")}
              </button>
            )}
          </div>
        </div>
      </section>
      )}

      {/* AMENITIES MARQUEE */}
      <div className="tp-marquee-section">
        <div className="tp-marquee-track">
          <div className="tp-marquee-content">
            {[...amenities, ...amenities].map(
              (item, index) => (
                <span
                  key={`${item}-${index}`}
                  className="tp-marquee-item"
                >
                  {item}
                </span>
              ),
            )}
          </div>
        </div>
      </div>

      {/* GROUNDS - PREMIUM LARGE CARDS */}
      {isWebsiteSectionVisible(website, "grounds") && (
      <section
        id="grounds"
        className="tp-section tp-bg-navy-dark"
      >
        <div className="tp-container">
          <div
            className="tp-section-header"
            data-animate="fade-up"
          >
            <p className="tp-eyebrow">
              {t("groundsEyebrow", "AVAILABLE COURTS")}
            </p>
            <h2 className={`tp-section-title${keepGroundsTitleTogether ? " tp-grounds-title-single-line" : ""}`}>
              {groundsTitle}
            </h2>
            <p className="tp-section-sub">
              {t(
                "groundsDescription",
                "Three premium indoor courts. One incredible venue.",
                apiGroundsDescription,
              )}
            </p>
          </div>

          <div className="tp-grounds-list">
            {groundCards.map((ground, index) => (
              <div
                key={ground.id}
                className="tp-ground-card"
                data-animate="fade-up"
                style={{
                  "--delay": `${index * 0.1}s`,
                }}
              >
                <div className="tp-ground-img-wrap">
                  <div
                    className="tp-ground-img"
                    style={{
                      backgroundImage:
                        `url(${ground.img})`,
                    }}
                  ></div>
                </div>

                <div className="tp-ground-info">
                  <div className="tp-ground-header">
                    <div>
                      <p className="tp-ground-sport">
                        {ground.sport}
                      </p>
                      <h3 className="tp-ground-title">
                        {ground.name}
                      </h3>
                    </div>
                    <div className="tp-ground-price">
                      {ground.price}
                    </div>
                  </div>

                  <button
                    className="tp-btn-primary tp-btn-sm"
                    onClick={() =>
                      openGroundBooking(ground.ground)
                    }
                  >
                    {t(
                      "groundBookLabel",
                      "Book This Court",
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ABOUT + STATS */}
      {isWebsiteSectionVisible(website, "about") && (
      <section
        id="about"
        className="tp-section tp-bg-dark tp-about-section"
      >
        <div className="tp-container">
          <div className="tp-about-grid">
            <div
              className="tp-about-text"
              data-animate="fade-right"
            >
              <p className="tp-eyebrow tp-eyebrow-green">
                {t("aboutEyebrow", "ABOUT TURF PARK")}
              </p>
              <h2 className="tp-section-title tp-title-light">
                {t("aboutTitleLineOne", "MORE THAN")}<br />
                {t("aboutTitleLineTwo", "A COURT.")}
              </h2>
              <p className="tp-about-desc">
                {resolvedAboutDescription}
              </p>
            </div>

            <div
              className="tp-stats-grid"
              ref={statsRef}
            >
              {stats.map((stat) => (
                <StatItem
                  key={stat.label}
                  {...stat}
                  isActive={statsActive}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONIALS */}
      {isWebsiteSectionVisible(website, "testimonials") && (
      <section className="tp-section tp-bg-light tp-testimonials-section">
        <div className="tp-container">
          <div
            className="tp-section-header"
            data-animate="fade-up"
          >
            <p className="tp-eyebrow">
              {t(
                "testimonialsEyebrow",
                "PLAYER REVIEWS",
              )}
            </p>
            <h2 className="tp-section-title">
              {t(
                "testimonialsTitle",
                "LOVED BY PLAYERS.",
              )}
            </h2>
          </div>

          <div className="tp-testimonials-grid">
            {testimonials.map(
              (testimonial, index) => (
                <div
                  key={`${testimonial.name}-${index}`}
                  className="tp-testimonial-card"
                  data-animate="fade-up"
                  style={{
                    "--delay": `${index * 0.12}s`,
                  }}
                >
                  <p className="tp-testimonial-text">
                    &quot;{testimonial.text}&quot;
                  </p>
                  <div className="tp-testimonial-author">
                    <div className="tp-author-avatar">
                      {testimonial.name[0]}
                    </div>
                    <div>
                      <p className="tp-author-name">
                        {testimonial.name}
                      </p>
                      <p className="tp-author-sport">
                        {testimonial.sport}
                      </p>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </section>
      )}

      {/* FAQ */}
      {isWebsiteSectionVisible(website, "faqs") && (
      <section className="tp-section tp-bg-dark tp-faq-section">
        <div className="tp-container tp-container-sm">
          <div
            className="tp-section-header"
            data-animate="fade-up"
          >
            <p className="tp-eyebrow tp-eyebrow-green">
              {t("faqEyebrow", "FAQ")}
            </p>
            <h2 className="tp-section-title tp-title-light">
              {t("faqTitle", "GOT QUESTIONS?")}
            </h2>
          </div>

          <div
            className="tp-faq-list"
            data-animate="fade-up"
          >
            {faqs.map((faq) => (
              <FaqItem key={faq.q} {...faq} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CONTACT - LET'S TALK */}
      {isWebsiteSectionVisible(website, "contact") && (
      <section
        id="contact"
        className="tp-section tp-bg-dark tp-contact-section"
      >
        <div className="tp-container">
          <div className="tp-talk-grid">
            <div
              className="tp-talk-left"
              data-animate="fade-right"
            >
              <p className="tp-eyebrow tp-eyebrow-green">
                {t(
                  "contactEyebrow",
                  "// GET IN TOUCH",
                )}
              </p>
              <h2 className="tp-talk-title">
                {t("contactTitleLineOne", "LET'S")}<br />
                {t("contactTitleLineTwo", "TALK.")}
              </h2>
              <p className="tp-talk-desc">
                {t(
                  "contactDescription",
                  "Have a question or want to discuss a corporate event? Send us a message and our team will get back to you shortly.",
                )}
              </p>

              <ul className="tp-talk-bullets">
                {[
                  t(
                    "contactBulletOne",
                    "Corporate Tournaments",
                  ),
                  t(
                    "contactBulletTwo",
                    "Birthday Parties & Events",
                  ),
                  t(
                    "contactBulletThree",
                    "Long-term Academy Bookings",
                  ),
                  t(
                    "contactBulletFour",
                    "Sponsorship Opportunities",
                  ),
                ].map((bullet) => (
                  <li key={bullet}>
                    <span className="tp-bullet-dot"></span>
                    {bullet}
                  </li>
                ))}
              </ul>

              <div className="tp-talk-quote">
                <p>
                  &quot;{t(
                    "contactQuote",
                    "The Turf Park team was incredibly helpful in organizing our annual corporate futsal tournament. Highly recommended!",
                  )}&quot;
                </p>
                <p className="tp-quote-author">
                  {t(
                    "contactQuoteAuthor",
                    "— Arun KC., Event Organizer",
                  )}
                </p>
              </div>
            </div>

            <div
              className="tp-talk-right"
              data-animate="fade-left"
            >
              <div className="tp-talk-form-card">
                <form
                  className="tp-talk-form"
                  onSubmit={handleFormSubmit}
                >
                  <PublicContactHoneypot value={formData.company} onChange={handleFormChange} />
                  <div className="tp-form-row">
                    <div className="tp-form-group">
                      <label>
                        {t(
                          "formNameLabel",
                          "FULL NAME *",
                        )}
                      </label>
                      <input
                        name="name"
                        type="text"
                        autoComplete="name"
                        maxLength={100}
                        placeholder={t(
                          "formNamePlaceholder",
                          "Your name",
                        )}
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="tp-form-group">
                      <label>
                        {t(
                          "formPhoneLabel",
                          "PHONE *",
                        )}
                      </label>
                      <PublicContactPhoneField
                        value={formData.phone}
                        countryCode={formData.phoneCountryCode}
                        onChange={handleFormChange}
                        placeholder={t(
                          "formPhonePlaceholder",
                          "+977 XXXXX XXXXX",
                          phonePlaceholder,
                        )}
                        required
                      />
                    </div>
                  </div>

                  <div className="tp-form-group">
                    <label>
                      {t(
                        "formEmailLabel",
                        "EMAIL *",
                      )}
                    </label>
                    <input
                      name="email"
                      type="email"
                      autoComplete="email"
                      maxLength={254}
                      placeholder={t(
                        "formEmailPlaceholder",
                        "you@email.com",
                      )}
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  <div className="tp-form-group">
                    <label>
                      {t(
                        "formMessageLabel",
                        "MESSAGE *",
                      )}
                    </label>
                    <textarea
                      name="message"
                      rows="4"
                      maxLength={1200}
                      placeholder={t(
                        "formMessagePlaceholder",
                        "How can we help you?",
                      )}
                      value={formData.message}
                      onChange={handleFormChange}
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="tp-btn-talk-submit"
                    disabled={contactSubmitting}
                  >
                    <span className="tp-icon-bolt">
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          d="M11 21l-1-1 2-7H8l1-1 9-9 1 1-2 7h4l-1 1-9 9z"
                          fill="#FF7B72"
                        />
                      </svg>
                    </span>
                    {contactSubmitting
                      ? "Sending…"
                      : t(
                          "formSubmitLabel",
                          "Send Message",
                        )}
                  </button>

                  <PublicContactEnquiryStatus
                    submitting={contactSubmitting}
                    feedback={contactFeedback}
                    comfortableSpacing
                  />

                  <p className="tp-form-footer">
                    {t(
                      "formFooter",
                      "We aim to respond within 24 hours.",
                    )}
                  </p>
                </form>
              </div>
            </div>
          </div>

          <div
            className="tp-location-banner"
            data-animate="fade-up"
          >
            <div className="tp-map-wrap" style={{ position: 'relative' }}>
              <iframe
                title={`${venueName} Location`}
                className="tp-map"
                loading="lazy"
                src={mapEmbedUrl}
                allowFullScreen
              ></iframe>
              <OpenMapsLink
                url={venue?.googleMapsUrl}
                latitude={venue?.location?.latitude}
                longitude={venue?.location?.longitude}
                venueName={venueName}
                label={t("openMapsLabel", "Open in Maps")}
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* PREMIUM APP CTA */}
      {isWebsiteSectionVisible(website, "app") && (
      <section className="tp-section tp-bg-navy-dark tp-app-section">
        <div className="tp-container">
          <div className="tp-premium-cta">
            <div
              className="tp-cta-text"
              data-animate="fade-right"
            >
              <p className="tp-eyebrow tp-eyebrow-green">
                {t("appEyebrow", "GET THE APP")}
              </p>
              <h2 className="tp-section-title tp-title-light">
                {t("appTitleLineOne", "BOOK FROM")}<br />
                {t("appTitleLineTwo", "ANYWHERE.")}
              </h2>
              <p className="tp-cta-desc">
                {resolvedAppDescription}
              </p>
              <div className="tp-cta-buttons">
                <a
                  href={appStoreUrl}
                  className="tp-cta-store-btn"
                >
                  <OngroundStoreIcon
                    platform="appStore"
                    className="tp-cta-store-icon"
                    monochromeColor="#ffffff"
                  />
                  <div>
                    <span className="tp-cta-store-label">
                      {t("appStoreSmallLabel", "Download on the")}
                    </span>
                    <span className="tp-cta-store-name">
                      {t("appStoreMainLabel", "App Store")}
                    </span>
                  </div>
                </a>

                <a
                  href={googlePlayUrl}
                  className="tp-cta-store-btn"
                >
                  <OngroundStoreIcon
                    platform="googlePlay"
                    className="tp-cta-store-icon"
                  />
                  <div>
                    <span className="tp-cta-store-label">
                      {t("googlePlaySmallLabel", "Get it on")}
                    </span>
                    <span className="tp-cta-store-name">
                      {t("googlePlayMainLabel", "Google Play")}
                    </span>
                  </div>
                </a>
              </div>
            </div>

            <div
              className="tp-cta-image"
              data-animate="fade-left"
            >
              <img
                src={resolvedAppImage}
                alt={t(
                  "appImageAlt",
                  "Onground App Mockup",
                )}
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* FOOTER */}
      <footer className="tp-footer">
        <div className="tp-container">
          <div className="tp-footer-grid">
            <div>
              <button
                className="tp-logo"
                onClick={scrollToTop}
              >
                <span className="tp-logo-dot"></span>
                <div>
                  <span className="tp-logo-name">
                    {brand.primary.toUpperCase()}
                    <span className="tp-logo-accent">
                      {brand.highlighted.toUpperCase()}
                    </span>
                  </span>
                  <span className="tp-logo-sub">
                    {venueLogoSub}
                  </span>
                </div>
              </button>
              <p className="tp-footer-tagline">
                {resolvedFooterTagline}
              </p>
            </div>

            <div>
              <p className="tp-footer-col-title">
                {t(
                  "footerNavigationTitle",
                  "Navigation",
                )}
              </p>
              <ul className="tp-footer-links">
                {navigation.map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() =>
                        scrollTo(link.id)
                      }
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="tp-footer-col-title">
                {t("footerContactTitle", "Contact")}
              </p>
              <ul className="tp-footer-links">
                <li><span>{venuePhone}</span></li>
                <li><span>{venueAddress}</span></li>
              </ul>
            </div>

            {footerSocialItems.length > 0 && (
              <div>
              <p className="tp-footer-col-title">
                {t("footerSocialTitle", "Follow Us")}
              </p>
              <ul className="tp-footer-links">
                {footerSocialItems.map((item) => (
                  <li key={item.key}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={item.label}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
              </div>
            )}
          </div>

          <div className="tp-footer-bottom">
            <p>
              © {new Date().getFullYear()} {venueName}
              {t(
                "footerCopyrightSuffix",
                ". All rights reserved.",
              )}
            </p>
            <a
              className="tp-footer-powered"
              href="https://getonground.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit Onground website"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "rgba(255,255,255,0.3)",
                fontSize: "0.950rem",
                textDecoration: "none",
              }}
            >
              {t("footerPoweredByLabel", "Powered by")}
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <img
                  src={resolvedPoweredLogo}
                  alt={t(
                    "poweredLogoAlt",
                    "ONGROUND",
                  )}
                  style={{ height: "22px" }}
                />
              </span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TurfParkTheme;
